"use client";

import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { usePresenceStore, type CustomStatusEntry } from "@/shared/model/presence-store";
import type { UserStatus } from "@/shared/ui/data-display/user-status";

type PresenceProfile = {
  id?: string | null;
  status?: string | null;
  statusExpiresAtUtc?: string | null;
  isOnline?: boolean;
  privacy?: { showOnlineStatus?: boolean };
  customStatusText?: string | null;
  customStatusEmoji?: string | null;
  customStatusExpiresAtUtc?: string | null;
};
const active = (expires?: string | null) => !expires || !Number.isFinite(Date.parse(expires)) || Date.parse(expires) > Date.now();

export function resolveUserStatus(profile: PresenceProfile | null | undefined, online: boolean, realtime?: { status: string; expiresAtUtc: string | null }, self = false): UserStatus {
  if (!profile?.id || (!self && profile.privacy?.showOnlineStatus === false)) return "offline";
  const raw = realtime ? (active(realtime.expiresAtUtc) ? realtime.status : undefined) : (active(profile.statusExpiresAtUtc) ? profile.status : undefined);
  if (raw === "invisible") return self ? "invisible" : "offline";
  if (raw === "away" || raw === "busy" || raw === "playing" || raw === "searching") return raw;
  return online ? "online" : "offline";
}

export function useUserPresence(profile: PresenceProfile | null | undefined, self = false) {
  const id = profile?.id ?? "";
  const { online, realtime, custom } = usePresenceStore(useShallow((s) => ({
    online: s.onlineUserIds.has(id), realtime: s.statusByUserId[id], custom: s.customStatusByUserId[id],
  })));
  const [, tick] = useState(0);
  const statusExpiry = realtime?.expiresAtUtc ?? profile?.statusExpiresAtUtc;
  const customExpiry = custom?.expiresAtUtc ?? profile?.customStatusExpiresAtUtc;
  useEffect(() => {
    const next = [statusExpiry, customExpiry].map((value) => value ? Date.parse(value) : NaN).filter((value) => Number.isFinite(value) && value > Date.now());
    if (!next.length) return;
    const timer = setTimeout(() => tick((n) => n + 1), Math.min(2_147_483_647, Math.max(1, Math.min(...next) - Date.now() + 1)));
    return () => clearTimeout(timer);
  });
  const source: CustomStatusEntry = custom ?? { text: profile?.customStatusText ?? null, emoji: profile?.customStatusEmoji ?? null, expiresAtUtc: profile?.customStatusExpiresAtUtc ?? null };
  const customStatus = (source.text || source.emoji) && active(source.expiresAtUtc) ? source : null;
  return { status: resolveUserStatus(profile, online, realtime, self), customStatus };
}
